/**
 * LanguageContent — web port of `apps/WunderGuide/components/LanguageSelector.tsx`.
 *
 * Matches the RN selector's design/layout/behaviour as closely as the web
 * drawer allows, reusing the same `languages.json` source so display names /
 * dialects / countries are identical:
 *   - Priority-tiered ordering (NO alphabetical headers): selected → device
 *     locale → recent → major → everything else, each tier separated by a thin
 *     untitled divider (exactly like RN's `groupDivider`).
 *   - Rows: leading checkmark column, native-name primary / english secondary,
 *     trailing dialect code with text/audio support indicators.
 *   - Sticky search that collapses the tiers into a flat alphabetical match
 *     list (selection always pinned on top), with a clear button.
 *
 * (No country flags — removed per product.)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import languagesJson from '../../data/languages.json';
import './LanguageContent.css';

interface Language {
  id: string;
  code: string;
  iso_639_2?: string;
  dialect?: string | null;
  country?: string | null;
  tld?: string | null;
  name: string;
  native_name?: string | null;
  label?: string;
  encoding?: string;
}

const ALL_LANGUAGES = languagesJson as unknown as Language[];

const RECENT_KEY = 'wg.recent_languages.v1';
const MAX_RECENT = 5;

// Mirrors RN's FALLBACK_MAJOR_LANGUAGES (the web client has no language-pack
// API, so we lean on the same static heuristic the RN selector falls back to).
const MAJOR_LANGUAGES = new Set([
  'en',
  'es',
  'fr',
  'de',
  'pt',
  'it',
  'ru',
  'ja',
  'ko',
  'zh',
  'ar',
  'hi',
  'nl',
  'pl',
  'tr',
  'vi',
  'th',
  'id',
  'uk',
]);

// Mirrors RN's FALLBACK_AUDIO_SUPPORTED — drives the trailing audio indicator.
const AUDIO_SUPPORTED = new Set([
  'af',
  'ar',
  'hy',
  'az',
  'be',
  'bs',
  'bg',
  'ca',
  'zh',
  'zh-Hans',
  'zh-Hant',
  'zh-CN',
  'zh-TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'et',
  'fi',
  'fr',
  'gl',
  'de',
  'el',
  'he',
  'hi',
  'hu',
  'is',
  'id',
  'it',
  'ja',
  'kn',
  'kk',
  'ko',
  'lv',
  'lt',
  'mk',
  'ms',
  'mr',
  'mi',
  'ne',
  'nb',
  'nn',
  'fa',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'es',
  'sw',
  'sv',
  'tl',
  'ta',
  'th',
  'tr',
  'uk',
  'ur',
  'vi',
  'cy',
]);

function displayName(lang: Language): string {
  return lang.label || lang.name || lang.code;
}

function langKey(lang: Language): string {
  return lang.dialect || lang.code;
}

function isMajor(lang: Language): boolean {
  const code = langKey(lang);
  return MAJOR_LANGUAGES.has(code) || MAJOR_LANGUAGES.has(code.split('-')[0]);
}

function hasAudio(lang: Language): boolean {
  const code = langKey(lang);
  return AUDIO_SUPPORTED.has(code) || AUDIO_SUPPORTED.has(code.split('-')[0]);
}

// Browser device locales (e.g. ["en-US", "en"]) — RN's device-locale priority.
function deviceTags(): string[] {
  if (typeof navigator === 'undefined') return [];
  const tags = [...(navigator.languages || []), navigator.language].filter(
    Boolean,
  ) as string[];
  return Array.from(new Set(tags));
}

function isMatchingId(
  selected: string | null | undefined,
  lang: Language,
): boolean {
  if (!selected) return false;
  return (
    selected === lang.id || selected === lang.dialect || selected === lang.code
  );
}

// RN priority tiers — kept identical so ordering matches the native client:
//   100 selected · 80 device · 70-75 recent · 60 major · 0 everything else.
function priorityOf(
  lang: Language,
  selected: string | null | undefined,
  recent: string[],
  tags: string[],
  searching: boolean,
): number {
  if (isMatchingId(selected, lang)) return 100;
  if (searching) return 0;

  const code = langKey(lang);
  if (
    tags.includes(code) ||
    (lang.dialect && tags.includes(lang.dialect)) ||
    (lang.code && tags.includes(lang.code))
  ) {
    return 80;
  }
  const recentIndex = recent.indexOf(code);
  if (recentIndex !== -1) return 70 + (MAX_RECENT - recentIndex);
  if (isMajor(lang)) return 60;
  return 0;
}

const LanguageContent: React.FC = () => {
  const { selectedLanguage, setSelectedLanguage } = useLanguage();
  const [search, setSearch] = useState('');
  const [recent, setRecent] = useState<string[]>([]);

  // Load recent languages
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          setRecent(parsed.filter(x => typeof x === 'string'));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleSelect = (lang: Language) => {
    const key = langKey(lang);
    setSelectedLanguage(key);
    try {
      const next = [key, ...recent.filter(r => r !== key)].slice(0, MAX_RECENT);
      setRecent(next);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  // Priority-sorted, tier-divided list (mirrors RN's single ScrollView). The
  // selected language is always pinned even when it falls outside the search.
  const ordered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searching = q.length > 0;
    const tags = deviceTags();

    let langs = ALL_LANGUAGES;
    if (searching) {
      langs = ALL_LANGUAGES.filter(
        l =>
          displayName(l).toLowerCase().includes(q) ||
          l.native_name?.toLowerCase().includes(q) ||
          l.country?.toLowerCase().includes(q) ||
          l.dialect?.toLowerCase().includes(q) ||
          l.code?.toLowerCase().includes(q),
      );
      const sel = ALL_LANGUAGES.find(l => isMatchingId(selectedLanguage, l));
      if (sel && !langs.some(l => l.id === sel.id)) langs = [sel, ...langs];
    }

    return [...langs].sort((a, b) => {
      const pa = priorityOf(a, selectedLanguage, recent, tags, searching);
      const pb = priorityOf(b, selectedLanguage, recent, tags, searching);
      if (pa !== pb) return pb - pa;
      return displayName(a).localeCompare(displayName(b));
    });
  }, [search, selectedLanguage, recent]);

  const searching = search.trim().length > 0;
  const tags = useMemo(() => deviceTags(), []);

  return (
    <div className="wg-lang">
      {/* Search */}
      <div className="wg-lang__search-wrap">
        <SearchIcon />
        <input
          className="wg-lang__search"
          type="search"
          placeholder="Search languages…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="wg-lang__clear"
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Priority-tiered list with dividers between tiers (RN parity) */}
      <div className="wg-lang__list">
        {ordered.map((lang, index) => {
          const selected = isMatchingId(selectedLanguage, lang);
          const prev = index > 0 ? ordered[index - 1] : null;
          const showDivider =
            !searching &&
            prev !== null &&
            Math.floor(
              priorityOf(prev, selectedLanguage, recent, tags, false) / 10,
            ) !==
              Math.floor(
                priorityOf(lang, selectedLanguage, recent, tags, false) / 10,
              );

          return (
            <React.Fragment key={`${lang.id}-${index}`}>
              {showDivider && <div className="wg-lang__divider" />}
              <Row
                lang={lang}
                selected={selected}
                onSelect={() => handleSelect(lang)}
              />
            </React.Fragment>
          );
        })}

        {ordered.length === 0 && (
          <div className="wg-lang__empty">No languages match "{search}".</div>
        )}
      </div>
    </div>
  );
};

export default LanguageContent;

const Row: React.FC<{
  lang: Language;
  selected: boolean;
  onSelect: () => void;
}> = ({ lang, selected, onSelect }) => {
  const native = lang.native_name || displayName(lang);
  const english = displayName(lang);
  const showSecondary = !!lang.native_name && english !== lang.native_name;
  const audio = hasAudio(lang);

  return (
    <button
      type="button"
      className={`wg-lang__row${selected ? ' is-selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="wg-lang__check">{selected ? <CheckIcon /> : null}</span>
      <span className="wg-lang__row-text">
        <span
          className={`wg-lang__row-native${selected ? ' is-selected' : ''}`}
        >
          {native}
        </span>
        {showSecondary && <span className="wg-lang__row-name">{english}</span>}
      </span>
      <span className="wg-lang__trailing">
        <span className="wg-lang__support">
          <TextIcon />
          {audio && <AudioIcon />}
        </span>
        <span className="wg-lang__code">{langKey(lang)}</span>
      </span>
    </button>
  );
};

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
// Text-support indicator (mirrors RN's "language" glyph).
function TextIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7V5h11v2" />
      <path d="M9 5v13" />
      <path d="M7 18h4" />
      <path d="M15 13h6" />
      <path d="M18 11v9" />
    </svg>
  );
}
// Audio-support indicator (mirrors RN's "volume-high" glyph).
function AudioIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="4 9 4 15 8 15 13 19 13 5 8 9 4 9" />
      <path d="M17 8a5 5 0 0 1 0 8" />
    </svg>
  );
}
